import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TRAVEL_REQUESTS } from "./travelData";
import "./travel.css";

/* Travel Requests listing — ported from the prototype (travel.html).
   Standard list design: tabs with counts → one-row filters → table,
   all inside one white container. Amend is only offered on Approved rows. */

const TABS = ["All", "Pending", "Approved", "Rejected", "Completed"];

const PILL_CLASS = {
  Approved: "pill-approved",
  Pending: "pill-pending",
  Rejected: "pill-rejected",
  Completed: "pill-completed",
};

const TravelListPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("All");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("All Dates");
  const [amendTarget, setAmendTarget] = useState(null);

  const counts = useMemo(() => {
    const byStatus = { All: TRAVEL_REQUESTS.length };
    TRAVEL_REQUESTS.forEach((row) => {
      byStatus[row.status] = (byStatus[row.status] || 0) + 1;
    });
    return byStatus;
  }, []);

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return TRAVEL_REQUESTS.filter((row) => {
      if (activeTab !== "All" && row.status !== activeTab) return false;
      if (!query) return true;
      return [row.id, row.destination, row.purpose].some((value) => value.toLowerCase().includes(query));
    });
  }, [activeTab, search]);

  const closeAmendModal = () => setAmendTarget(null);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeAmendModal();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const resetFilters = () => {
    setSearch("");
    setDateFilter("All Dates");
    setActiveTab("All");
  };

  return (
    <div className="bes-travel">
        <div className="travel-page-head">
          <div>
            <h1>Travel Requests</h1>
            <p>Manage travel requests, cash advances, arrangements, and budget tracking.</p>
          </div>
          <button type="button" className="btn btn-dark" onClick={() => navigate("/travel/create")}>
            <i className="ri-add-line" /> New Travel Request
          </button>
        </div>

        <div className="list-container">
          <div className="list-container-inner">
            <div className="approval-tabs">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={`approval-tab ${activeTab === tab ? "active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab} <span className="count">{counts[tab] || 0}</span>
                </button>
              ))}
            </div>

            <div className="filters-standard">
              <div className="filter-group search">
                <label>Search</label>
                <i className="ri-search-line" />
                <input
                  className="form-control"
                  type="text"
                  placeholder="Search reference, destination or purpose..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              <div className="filter-group">
                <label>Submitted Date</label>
                <select className="form-control" value={dateFilter} onChange={(event) => setDateFilter(event.target.value)}>
                  <option>All Dates</option>
                  <option>Today</option>
                  <option>Last 7 Days</option>
                  <option>Last 30 Days</option>
                </select>
              </div>
              <button type="button" className="btn btn-outline" onClick={resetFilters}>
                <i className="ri-refresh-line" /> Reset
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="data-table travel-table">
              <thead>
                <tr>
                  <th>
                    Request ID <i className="ri-arrow-up-line th-sort-icon" />
                  </th>
                  <th>Destination / Purpose</th>
                  <th>Departure</th>
                  <th>Return</th>
                  <th className="text-right">Amount (RM)</th>
                  <th>Status</th>
                  <th>Last updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="font-medium">{row.id}</td>
                    <td>
                      {row.destination} · {row.purpose}
                    </td>
                    <td>{row.departure}</td>
                    <td>{row.ret}</td>
                    <td className="text-right font-medium">{row.amount}</td>
                    <td>
                      <span className={`status-pill ${PILL_CLASS[row.status] || "pill-draft"}`}>
                        <i className="ri-circle-fill" /> {row.status}
                      </span>
                    </td>
                    <td className="text-muted">{row.updated}</td>
                    <td className="actions-col">
                      <div className="travel-actions">
                        <button
                          type="button"
                          className="travel-action-btn"
                          title="View request"
                          onClick={() => navigate(`/travel/${row.id}`)}
                        >
                          <i className="ri-eye-line" /> View
                        </button>
                        {row.status === "Approved" && (
                          <button
                            type="button"
                            className="travel-action-btn amend"
                            title="Amend approved request"
                            onClick={() => setAmendTarget(row.id)}
                          >
                            <i className="ri-edit-line" /> Amend
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-muted empty-row">
                      No travel requests match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-container">
            <span className="showing-text text-muted">
              Showing {rows.length ? 1 : 0} - {rows.length} of {rows.length} results
            </span>
            <div className="pagination">
              <button type="button" className="page-btn">
                <i className="ri-arrow-left-s-line" />
              </button>
              <button type="button" className="page-btn active">
                1
              </button>
              <button type="button" className="page-btn">
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>
        </div>

        {amendTarget && (
          <div
            className="amend-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="amendModalTitle"
            onClick={(event) => {
              if (event.target === event.currentTarget) closeAmendModal();
            }}
          >
            <div className="amend-modal">
              <div className="amend-modal-header">
                <div className="amend-modal-icon">
                  <i className="ri-alert-fill" />
                </div>
                <div>
                  <h3 id="amendModalTitle">Amend Approved Travel Request?</h3>
                  <p className="amend-modal-ref">Request {amendTarget}</p>
                </div>
              </div>
              <div className="amend-modal-body">
                Use this function only for urgent or exceptional changes after approval, such as flight cancellation,
                schedule disruption, emergency accommodation changes, or other unavoidable circumstances.
                <div className="amend-rule">
                  <strong>CEO approval required.</strong>
                  <br />
                  Once the amended request is submitted, its status will change to <strong>Pending CEO Approval</strong>.
                  Existing approved arrangements remain recorded in the request history.
                </div>
              </div>
              <div className="amend-modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeAmendModal}>
                  No, Keep Request
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate(`/travel/${amendTarget}/amend`)}
                >
                  Proceed to Amend
                </button>
              </div>
            </div>
          </div>
      )}
    </div>
  );
};

export default TravelListPage;
